import { useMemo } from "react";
import type { ConnectorResponse } from "@/api";
import {
  addConnectorTeamMember,
  addConnectorUserMember,
  getConnectorMembersOptions,
  getConnectorMembersQueryKey,
  removeConnectorTeamMember,
  removeConnectorUserMember,
  updateConnectorTeamMember,
  updateConnectorUserMember,
} from "@/api";
import { type MembershipApi, MembershipPanel } from "@/components/build/MembershipPanel";
import { useCurrentUser } from "@/features/account/useCurrentUser";

/** Who can use a connector: people and teams (viewer < editor < admin), via the shared MembershipPanel. */
export function ConnectorAccessTab({ connector }: Readonly<{ connector: ConnectorResponse }>) {
  const me = useCurrentUser();
  const uuid = connector.uuid;
  const api = useMemo<MembershipApi>(
    () => ({
      membersOptions: () => getConnectorMembersOptions({ path: { connector_uuid: uuid } }),
      membersQueryKey: () => getConnectorMembersQueryKey({ path: { connector_uuid: uuid } }),
      addUser: (id, role) =>
        addConnectorUserMember({ path: { connector_uuid: uuid }, body: { userUuid: id, role }, throwOnError: true }),
      updateUserRole: (id, role) =>
        updateConnectorUserMember({
          path: { connector_uuid: uuid, user_uuid: id },
          body: { role },
          throwOnError: true,
        }),
      removeUser: (id) =>
        removeConnectorUserMember({ path: { connector_uuid: uuid, user_uuid: id }, throwOnError: true }),
      addTeam: (id, role) =>
        addConnectorTeamMember({ path: { connector_uuid: uuid }, body: { teamUuid: id, role }, throwOnError: true }),
      updateTeamRole: (id, role) =>
        updateConnectorTeamMember({
          path: { connector_uuid: uuid, team_uuid: id },
          body: { role },
          throwOnError: true,
        }),
      removeTeam: (id) =>
        removeConnectorTeamMember({ path: { connector_uuid: uuid, team_uuid: id }, throwOnError: true }),
    }),
    [uuid],
  );
  return <MembershipPanel api={api} currentUserUuid={me.data?.user.uuid} />;
}
